<script setup lang="ts">
/**
 * Toast area of the application shell.
 *
 * Mounted once in App.vue and teleported to the document body so a toast is
 * never clipped by a scrolling container. The region is polite: it announces
 * the message without interrupting the advisor, and it never receives the focus
 * on its own. Each toast disappears on its own after four seconds and can be
 * dismissed earlier.
 */
import { useToasts } from '@/composables/useToasts';

const { toasts, retirer } = useToasts();

const CLASSE_PAR_TON = {
  succes: 'hz-toast--succes',
  erreur: 'hz-toast--erreur',
  information: 'hz-toast--information',
} as const;
</script>

<template>
  <Teleport to="body">
    <div
      class="hz-toasts"
      role="region"
      aria-label="Notifications éphémères"
      aria-live="polite"
    >
      <div
        v-for="toast in toasts"
        :key="toast.id"
        class="hz-toast"
        :class="CLASSE_PAR_TON[toast.ton]"
        role="status"
      >
        <div class="hz-toast__corps">
          <p>{{ toast.message }}</p>
          <p
            v-if="toast.detail"
            class="hz-toast__detail"
          >
            {{ toast.detail }}
          </p>
        </div>
        <button
          type="button"
          class="hz-toast__fermer"
          @click="retirer(toast.id)"
        >
          <svg
            viewBox="0 0 12 12"
            aria-hidden="true"
            focusable="false"
          >
            <line
              x1="2"
              y1="2"
              x2="10"
              y2="10"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <line
              x1="10"
              y1="2"
              x2="2"
              y2="10"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
          <span class="hz-visually-hidden">Fermer la notification</span>
        </button>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.hz-toast__fermer svg {
  width: 12px;
  height: 12px;
  display: block;
}
</style>
